import * as React from 'react';
import * as Classes from '../common/classes';
import classNames from 'classnames';
import { LabelValueListItemProps } from './LabelValueList';
import { IBaseTextProps } from '../typography/Text';
import { useInverseBg } from '../theme/useInverseBg';
import { FormGroup } from '../form-group/FormGroup';

const EMPTY_VALUE = '-';

export interface ILabelValueProps extends IBaseTextProps {
  /**
   * Inverzní verze - typicky používáme na tmavším pozadí.
   */
  inverse?: boolean;
  /**
   * Pokud není uveden, vykreslí se pouze hodnota - někdy je label zbytečný a je vhodné použít tuto komponentu už kvůli stylu hodnoty.
   */
  label?: React.ReactNode;
  /**
   * Pokud title není uvedeno, zobrazuje se po najetí na hodnotu text z label.
   * Pokud title je uvedeno, zobrazuje se po najetí jak na label, tak na hodnotu.
   */
  title?: string;
  /**
   * Jen pro variantu mimo LabelValueList.
   */
  labelClassName?: string;
  contentClassName?: string;
  /**
   * Jen pro variantu mimo LabelValueList.
   */
  horizontal?: boolean;
  /**
   * Co se má zobrazit při prázdné hodnotě? Ve vhochozím stavu se použije '-'.
   */
  emptyValue?: React.ReactNode;
  /**
   * Jen pro variantu mimo LabelValueList.
   */
  lastItem?: boolean;

  /**
   * Vycentruje horionztálně label, nefunguje pokud je horizontal true.
   */
  centeredLabel?: boolean;
}

type LabelValueProps = React.PropsWithChildren<ILabelValueProps>;

type Props = LabelValueProps | LabelValueListItemProps;

export const LabelValue: React.FC<Props> = ({
  inverse,
  label,
  title,
  children,
  style,
  intent,
  italic,
  strong,
  className,
  underline,
  contentClassName,
  labelClassName,
  lastItem,
  horizontal = false,
  emptyValue = EMPTY_VALUE,
  centeredLabel,
}) => {
  const parentInverse = useInverseBg(inverse);

  let valueCls = classNames(contentClassName, Classes.LABEL_VALUE_CONTENT, {
    [Classes.TYPOGRAPHY_INHERIT]: !intent || intent === 'inherit',
    [Classes.TYPOGRAPHY_PRIMARY]: intent === 'primary',
    [Classes.TYPOGRAPHY_SECONDARY]: intent === 'secondary',
    [Classes.TYPOGRAPHY_DANGER]: intent === 'danger',
    [Classes.TYPOGRAPHY_SUCCESS]: intent === 'success',
    [Classes.TYPOGRAPHY_WARNING]: intent === 'warning',
    [Classes.TYPOGRAPHY_INACTIVE]: intent === 'inactive',
    [Classes.TYPOGRAPHY_DEFAULT]: intent === 'default',
    [Classes.TYPOGRAPHY_INVERSE]: parentInverse,
    [Classes.TYPOGRAPHY_STRONG]: strong,
    [Classes.TYPOGRAPHY_ITALIC]: italic,
    [Classes.TYPOGRAPHY_UNDERLINE]: underline,
  });

  let realValue;
  if (children === '' || children === null || children === undefined) {
    realValue = (
      <span
        title={title || (typeof label !== 'object' ? (label as string) : '')}
      >
        {emptyValue}
      </span>
    );
  } else {
    realValue = (
      <span
        title={title || (typeof label !== 'object' ? (label as string) : '')}
      >
        {children}
      </span>
    );
  }

  return (
    <FormGroup
      style={style}
      label={
        <span
          className={classNames(
            [Classes.LABEL],
            {
              [Classes.TYPOGRAPHY_INACTIVE]: intent === 'inactive',
              [Classes.TYPOGRAPHY_INVERSE]: parentInverse,
              [Classes.LABEL_VALUE_CENTRED]: centeredLabel && !horizontal,
            },
            labelClassName
          )}
          title={title}
        >
          {label}
        </span>
      }
      className={classNames(Classes.LABEL_VALUE, className, {
        [Classes.LABEL_VALUE_INLINE]: horizontal,
        [Classes.LABEL_VALUE_LAST_ITEM]: lastItem,
      })}
      contentClassName={valueCls}
      inline={horizontal}
    >
      {realValue}
    </FormGroup>
  );
};
